import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { EmptyState } from "@/modules/shell/EmptyState";
import { d } from "@icraftx/shared/v1";
import type { PosCatalogItem } from "@/shared/api/pos";
import { filterCatalogBrowse } from "../catalog-rail-math";
import { PosFloorSheet } from "./PosFloorSheet";
import { PosProductTile } from "./PosProductTile";

/** Full-screen catalog sheet — plan/35 phone Floor Browse. */
export function PosCatalogBrowseSheet(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  catalog: PosCatalogItem[];
  cartQtyByProduct: Map<string, number>;
  favoriteIds: ReadonlySet<string>;
  drawerOpen: boolean;
  busy: boolean;
  initialQuery?: string;
  onAdd: (p: PosCatalogItem) => void;
  onToggleFavorite: (productId: string) => void;
}) {
  const [query, setQuery] = useState(props.initialQuery ?? "");

  useEffect(() => {
    if (props.open) setQuery(props.initialQuery ?? "");
  }, [props.open, props.initialQuery]);

  const filtered = useMemo(
    () => filterCatalogBrowse(props.catalog, query),
    [props.catalog, query],
  );

  const cartUnits = useMemo(() => {
    let n = 0;
    for (const q of props.cartQtyByProduct.values()) n += q;
    return n;
  }, [props.cartQtyByProduct]);

  return (
    <PosFloorSheet
      open={props.open}
      onOpenChange={props.onOpenChange}
      title="Browse"
      description="Search the product catalog and add items to the cart"
      trailing={
        <Button
          type="button"
          className="min-h-11"
          onClick={() => props.onOpenChange(false)}
        >
          Done{cartUnits > 0 ? ` · ${cartUnits}` : ""}
        </Button>
      }
    >
      <div className="mb-3">
        <Input
          className="min-h-11 font-mono"
          placeholder="Search name, SKU, barcode"
          value={query}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          autoFocus
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {filtered.length === 0 ? (
        <EmptyState
          title={props.catalog.length === 0 ? "No products" : "No matches"}
          description={
            props.catalog.length === 0
              ? "Add products and a POS list price before ringing sales."
              : "Try another name, SKU, or barcode."
          }
        />
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {filtered.map((p) => (
            <PosProductTile
              key={p.id}
              product={p}
              cartQty={props.cartQtyByProduct.get(p.id) ?? 0}
              favorited={props.favoriteIds.has(p.id)}
              disabled={
                !props.drawerOpen || props.busy || d(p.price).lte(0)
              }
              onAdd={() => props.onAdd(p)}
              onToggleFavorite={() => props.onToggleFavorite(p.id)}
            />
          ))}
        </div>
      )}
    </PosFloorSheet>
  );
}
